import { Check, Copy } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

/**
 * Copy one string, and say so.
 *
 * The confirmation is the point. A copy button with no feedback leaves the
 * reader to paste somewhere and check, which is slower than selecting the text
 * by hand — so this swaps to a tick for a moment and announces the change to
 * assistive tech through the live region below.
 *
 * `navigator.clipboard` is unavailable outside a secure context (a plain-http
 * origin that is not localhost — a tunnelled preview, for instance), so the
 * legacy `execCommand` path is kept as a fallback rather than letting the
 * button silently do nothing there.
 */
export function CopyButton({
  value,
  label,
  className = '',
}: {
  value: string;
  /** What is being copied, for the button's accessible name. */
  label: string;
  className?: string;
}) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A row can unmount while the tick is still showing — a scroll, a refetch, a
  // filter toggle — and setting state afterwards is a React warning and a leak.
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const flash = (next: 'copied' | 'failed') => {
    setState(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setState('idle'), 1500);
  };

  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        flash('copied');
        return;
      }
      const field = document.createElement('textarea');
      field.value = value;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.appendChild(field);
      field.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(field);
      flash(ok ? 'copied' : 'failed');
    } catch {
      flash('failed');
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={copy}
        aria-label={state === 'copied' ? `${label} copied` : `Copy ${label}`}
        title={state === 'failed' ? 'Copy failed' : `Copy ${label}`}
        className={`rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-200 ${
          state === 'copied' ? 'text-emerald-600 dark:text-emerald-400' : ''
        } ${state === 'failed' ? 'text-red-600 dark:text-red-400' : ''} ${className}`}
      >
        {state === 'copied' ? (
          <Check aria-hidden className="h-3.5 w-3.5" />
        ) : (
          <Copy aria-hidden className="h-3.5 w-3.5" />
        )}
      </button>
      <span aria-live="polite" className="sr-only">
        {state === 'copied' ? `${label} copied to clipboard` : ''}
        {state === 'failed' ? `Could not copy ${label}` : ''}
      </span>
    </>
  );
}
